import { env, joinApiUrl } from '../config/env';
import { clearTokens, loadTokens, saveTokens } from '../features/auth/authStorage';

import type { TokenPairResponse } from './auth';

/** Dispatched after `saveTokens` when the access token is renewed via refresh. */
export const AUTH_TOKENS_UPDATED_EVENT = 'waf-crm-auth-refreshed';

/** Dispatched when the backend definitively rejected the stored refresh token. */
export const AUTH_SESSION_EXPIRED_EVENT = 'waf-crm-auth-expired';

export type RefreshOutcome =
  | { status: 'refreshed'; accessToken: string }
  /** Backend rejected the refresh token; stored tokens were cleared. */
  | { status: 'expired' }
  /** Backend unreachable or failing; tokens are kept so the session can resume. */
  | { status: 'unavailable' };

/** Internal marker: the stored token changed mid-flight, so the attempt is worth repeating. */
type RefreshAttempt = RefreshOutcome | { status: 'stale' };

// A refresh racing another browser tab can be rejected because that tab already
// rotated the token. One extra attempt picks up the newer token from storage.
const MAX_ATTEMPTS = 2;

let inFlight: Promise<RefreshOutcome> | null = null;

async function postRefresh(refreshToken: string): Promise<Response | null> {
  try {
    return await fetch(joinApiUrl(env.apiBaseUrl, '/auth/refresh'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
  } catch {
    // Offline, DNS/TLS failure, aborted request: never a reason to drop the session.
    return null;
  }
}

async function refreshOnce(): Promise<RefreshAttempt> {
  const stored = loadTokens();
  if (!stored?.refresh_token) {
    return { status: 'expired' };
  }

  const response = await postRefresh(stored.refresh_token);
  if (!response) {
    return { status: 'unavailable' };
  }

  if (response.ok) {
    const data = (await response.json()) as TokenPairResponse;
    saveTokens(data);
    window.dispatchEvent(new CustomEvent(AUTH_TOKENS_UPDATED_EVENT));
    return { status: 'refreshed', accessToken: data.access_token };
  }

  if (response.status !== 401) {
    // 5xx during a deploy, 502 from the proxy, rate limiting: retry later, stay logged in.
    return { status: 'unavailable' };
  }

  const latest = loadTokens();
  if (latest?.refresh_token && latest.refresh_token !== stored.refresh_token) {
    // Another tab rotated the token while this request was in flight; the newer
    // token is still valid, so retry instead of ending the session.
    return { status: 'stale' };
  }

  clearTokens();
  window.dispatchEvent(new CustomEvent(AUTH_SESSION_EXPIRED_EVENT));
  return { status: 'expired' };
}

/**
 * Calls `/auth/refresh` using the refresh token from storage.
 * Single-flight: concurrent 401s share one refresh (server rotates the refresh token on success).
 * Tokens are cleared only when the backend itself rejects them — transport and
 * server errors leave the session intact.
 */
export function refreshSessionSingleFlight(): Promise<RefreshOutcome> {
  if (!inFlight) {
    inFlight = (async () => {
      for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt += 1) {
        const outcome = await refreshOnce();
        if (outcome.status !== 'stale') {
          return outcome;
        }
      }
      return { status: 'unavailable' } as RefreshOutcome;
    })().finally(() => {
      inFlight = null;
    });
  }

  return inFlight;
}

/** Convenience wrapper for `apiRequest`: the renewed access token, or null when unavailable. */
export async function refreshAccessTokenSingleFlight(): Promise<string | null> {
  const outcome = await refreshSessionSingleFlight();
  return outcome.status === 'refreshed' ? outcome.accessToken : null;
}
